import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  
  // Add readonly token if in readonly session
  const savedSession = sessionStorage.getItem('readonly_session');
  if (savedSession) {
    try {
      const { token: readonlyToken } = JSON.parse(savedSession);
      if (readonlyToken) {
        headers['X-Readonly-Token'] = readonlyToken;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  
  return headers;
};

export const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Blood Results
export const createBloodResult = async (data) => {
  const response = await axios.post(`${API_URL}/blood-results`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getBloodResults = async () => {
  const response = await axios.get(`${API_URL}/blood-results`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateBloodResult = async (id, data) => {
  const response = await axios.put(`${API_URL}/blood-results/${id}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteBloodResult = async (id) => {
  const response = await axios.delete(`${API_URL}/blood-results/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Symptoms
export const createSymptom = async (data) => {
  const response = await axios.post(`${API_URL}/symptoms`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getSymptoms = async () => {
  const response = await axios.get(`${API_URL}/symptoms`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateSymptom = async (id, data) => {
  const response = await axios.put(`${API_URL}/symptoms/${id}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteSymptom = async (id) => {
  const response = await axios.delete(`${API_URL}/symptoms/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Medications
export const createMedication = async (data) => {
  const response = await axios.post(`${API_URL}/medications`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getMedications = async () => {
  const response = await axios.get(`${API_URL}/medications`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateMedication = async (id, data) => {
  const response = await axios.put(`${API_URL}/medications/${id}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteMedication = async (id) => {
  const response = await axios.delete(`${API_URL}/medications/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Alerts
export const getAlerts = async () => {
  const response = await axios.get(`${API_URL}/alerts`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const markAlertRead = async (id) => {
  const response = await axios.patch(`${API_URL}/alerts/${id}/read`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Glossary
export const getGlossary = async () => {
  const response = await axios.get(`${API_URL}/glossary`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Export PDF
export const exportPDF = async (startDate, endDate) => {
  const response = await axios.get(
    `${API_URL}/export/pdf?start_date=${startDate}&end_date=${endDate}`,
    {
      headers: getAuthHeader(),
      responseType: 'blob'
    }
  );
  return response.data;
};

// Get earliest record date for "All" export option
export const getEarliestRecordDate = async () => {
  const response = await axios.get(`${API_URL}/export/earliest-date`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Nutrition & Deficiencies
export const getNutritionRecords = async () => {
  const response = await axios.get(`${API_URL}/nutrition`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createNutritionRecord = async (data) => {
  const response = await axios.post(`${API_URL}/nutrition`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateNutritionRecord = async (id, data) => {
  const response = await axios.put(`${API_URL}/nutrition/${id}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteNutritionRecord = async (id) => {
  const response = await axios.delete(`${API_URL}/nutrition/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// User Profile
export const changePassword = async (currentPassword, newPassword) => {
  const response = await axios.put(
    `${API_URL}/user/password`,
    {
      current_password: currentPassword,
      new_password: newPassword
    },
    {
      headers: getAuthHeader()
    }
  );
  return response.data;
};

export const previewCountryChange = async (newCountry) => {
  const response = await axios.post(
    `${API_URL}/user/country/preview`,
    {
      new_country: newCountry
    },
    {
      headers: getAuthHeader()
    }
  );
  return response.data;
};

export const changeCountry = async (newCountry) => {
  const response = await axios.put(
    `${API_URL}/user/country`,
    {
      new_country: newCountry
    },
    {
      headers: getAuthHeader()
    }
  );
  return response.data;
};

// Admin
export const getAdminUsers = async (params) => {
  const queryString = new URLSearchParams(
    Object.entries(params).filter(([_, v]) => v != null)
  ).toString();
  
  const response = await axios.get(
    `${API_URL}/admin/users?${queryString}`,
    {
      headers: getAuthHeader()
    }
  );
  return response.data;
};

export const updateUserRole = async (userId, role) => {
  const response = await axios.put(
    `${API_URL}/admin/users/${userId}/role`,
    { role },
    {
      headers: getAuthHeader()
    }
  );
  return response.data;
};

export const unlockUserAccount = async (userId) => {
  const response = await axios.post(
    `${API_URL}/admin/users/${userId}/unlock`,
    {},
    {
      headers: getAuthHeader()
    }
  );
  return response.data;
};

export const resetUserPassword = async (userId) => {
  const response = await axios.post(
    `${API_URL}/admin/users/${userId}/reset-password`,
    {},
    {
      headers: getAuthHeader()
    }
  );
  return response.data;
};

export const startReadOnlySession = async (userId) => {
  const response = await axios.post(
    `${API_URL}/admin/users/${userId}/start-readonly-session`,
    {},
    {
      headers: getAuthHeader()
    }
  );
  return response.data;
};

export const endReadOnlySession = async (userId) => {
  const response = await axios.post(
    `${API_URL}/admin/users/${userId}/end-readonly-session`,
    {},
    {
      headers: getAuthHeader()
    }
  );
  return response.data;
};

// Admin: Upgrade/Downgrade user plan
export const upgradeUserPlan = async (userId) => {
  const response = await axios.post(
    `${API_URL}/admin/users/${userId}/upgrade`,
    {},
    {
      headers: getAuthHeader()
    }
  );
  return response.data;
};

export const downgradeUserPlan = async (userId) => {
  const response = await axios.post(
    `${API_URL}/admin/users/${userId}/downgrade`,
    {},
    {
      headers: getAuthHeader()
    }
  );
  return response.data;
};

// Delete user (admin only)
export const deleteUser = async (userId) => {
  const response = await axios.delete(
    `${API_URL}/admin/users/${userId}`,
    {
      headers: getAuthHeader()
    }
  );
  return response.data;
};

// Supplements API (Isolated Module)
export const getSupplements = async () => {
  const response = await axios.get(`${API_URL}/supplements`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Get ALL treatments (medications + vitamins + supplements) for timeline
export const getAllTreatments = async () => {
  const response = await axios.get(`${API_URL}/treatments/all`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createSupplement = async (data) => {
  const response = await axios.post(`${API_URL}/supplements`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateSupplement = async (id, data) => {
  const response = await axios.put(`${API_URL}/supplements/${id}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteSupplement = async (id) => {
  const response = await axios.delete(`${API_URL}/supplements/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Vitamins API (Duplicates Medications structure)
export const getVitamins = async () => {
  const response = await axios.get(`${API_URL}/vitamins`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createVitamin = async (data) => {
  const response = await axios.post(`${API_URL}/vitamins`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateVitamin = async (id, data) => {
  const response = await axios.put(`${API_URL}/vitamins/${id}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteVitamin = async (id) => {
  const response = await axios.delete(`${API_URL}/vitamins/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};


// Onboarding
export const completeOnboarding = async () => {
  const response = await axios.put(`${API_URL}/user/onboarding`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// First Return (Day 2) Experience
export const markFirstReturnSeen = async () => {
  const response = await axios.put(`${API_URL}/user/first-return`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Update user locale (country/region for unit formatting)
export const updateUserLocale = async (locale) => {
  const response = await axios.put(`${API_URL}/user/locale`, { locale }, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Account Deletion
export const deleteAccount = async () => {
  const response = await axios.delete(`${API_URL}/user/account`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// User Usage Stats (for plan gating)
export const getUserUsage = async () => {
  const response = await axios.get(`${API_URL}/user/usage`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Mood Check-in
export const saveMood = async (score, date = null) => {
  const data = { score };
  if (date) data.date = date;
  
  const response = await axios.post(`${API_URL}/mood`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getTodayMood = async () => {
  const response = await axios.get(`${API_URL}/mood/today`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getMoods = async (days = 30) => {
  const response = await axios.get(`${API_URL}/mood?days=${days}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Ideas & Research
export const saveIdea = async (data) => {
  const response = await axios.post(`${API_URL}/ideas`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getIdeas = async () => {
  const response = await axios.get(`${API_URL}/ideas`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const checkIdeaSaved = async (sourceUrl) => {
  const response = await axios.get(`${API_URL}/ideas/check?source_url=${encodeURIComponent(sourceUrl)}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateIdea = async (id, data) => {
  const response = await axios.patch(`${API_URL}/ideas/${id}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteIdea = async (id) => {
  const response = await axios.delete(`${API_URL}/ideas/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};
// ============================================================================
// BLOG API
// ============================================================================

// Admin: Create blog post
export const createBlogPost = async (data) => {
  const response = await axios.post(`${API_URL}/admin/blog`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Admin: Get all blog posts (including unpublished)
export const getAdminBlogPosts = async () => {
  const response = await axios.get(`${API_URL}/admin/blog`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Admin: Update blog post
export const updateBlogPost = async (id, data) => {
  const response = await axios.patch(`${API_URL}/admin/blog/${id}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Admin: Delete blog post
export const deleteBlogPost = async (id) => {
  const response = await axios.delete(`${API_URL}/admin/blog/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Public: Get published blog posts
export const getPublishedBlogPosts = async (limit = 20, skip = 0) => {
  const response = await axios.get(`${API_URL}/blog?limit=${limit}&skip=${skip}`);
  return response.data;
};

// Public: Get single blog post by slug
export const getBlogPostBySlug = async (slug) => {
  const response = await axios.get(`${API_URL}/blog/${slug}`);
  return response.data;
};

// Password Reset (public - no auth required)
export const requestPasswordReset = async (email) => {
  const response = await axios.post(`${API_URL}/auth/request-password-reset`, { email });
  return response.data;
};

export const confirmPasswordReset = async (token, newPassword) => {
  const response = await axios.post(`${API_URL}/auth/reset-password`, {
    token,
    new_password: newPassword
  });
  return response.data;
};

// Notifications API
export const getNotifications = async () => {
  const response = await axios.get(`${API_URL}/notifications`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getUnreadNotificationCount = async () => {
  const response = await axios.get(`${API_URL}/notifications/unread-count`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const markNotificationRead = async (notificationId) => {
  const response = await axios.post(`${API_URL}/notifications/${notificationId}/read`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await axios.post(`${API_URL}/notifications/mark-all-read`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getEmailPreferences = async () => {
  const response = await axios.get(`${API_URL}/user/email-preferences`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateEmailPreferences = async (frequency) => {
  const response = await axios.patch(`${API_URL}/user/email-preferences`, { frequency }, {
    headers: getAuthHeader()
  });
  return response.data;
};

// News Crawler API (Admin)
export const getNewsSources = async () => {
  const response = await axios.get(`${API_URL}/admin/news/sources`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createNewsSource = async (data) => {
  const response = await axios.post(`${API_URL}/admin/news/sources`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateNewsSource = async (sourceId, data) => {
  const response = await axios.patch(`${API_URL}/admin/news/sources/${sourceId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteNewsSource = async (sourceId) => {
  const response = await axios.delete(`${API_URL}/admin/news/sources/${sourceId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const triggerCrawl = async (sourceId = null) => {
  const url = sourceId 
    ? `${API_URL}/admin/news/crawl/${sourceId}` 
    : `${API_URL}/admin/news/crawl`;
  const response = await axios.post(url, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getCrawledArticles = async (status = 'pending') => {
  const response = await axios.get(`${API_URL}/admin/news/articles?status=${status}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const approveArticle = async (articleId, publishDate = null) => {
  const url = publishDate 
    ? `${API_URL}/admin/news/articles/${articleId}/approve?publish_date=${publishDate}`
    : `${API_URL}/admin/news/articles/${articleId}/approve`;
  const response = await axios.post(url, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const rejectArticle = async (articleId) => {
  const response = await axios.post(`${API_URL}/admin/news/articles/${articleId}/reject`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteNewsPost = async (postId) => {
  const response = await axios.delete(`${API_URL}/admin/news/posts/${postId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const searchNewsPosts = async (query) => {
  const response = await axios.get(`${API_URL}/admin/news/posts/search?q=${encodeURIComponent(query)}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteNewsPostBySlug = async (slug) => {
  const response = await axios.delete(`${API_URL}/admin/news/posts/by-slug/${encodeURIComponent(slug)}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getAllNewsPosts = async () => {
  const response = await axios.get(`${API_URL}/news`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Admin Content Calendar
export const getContentCalendar = async (start, end) => {
  const response = await axios.get(`${API_URL}/admin/content-calendar?start=${start}&end=${end}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const rescheduleContent = async (contentType, itemId, publishDate) => {
  const response = await axios.patch(
    `${API_URL}/admin/content-calendar/${contentType}/${itemId}/reschedule`,
    { publish_date: publishDate },
    { headers: getAuthHeader() }
  );
  return response.data;
};

// Public News API (for members)
export const getPublishedNews = async () => {
  const response = await axios.get(`${API_URL}/news`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const markNewsRead = async () => {
  const response = await axios.post(`${API_URL}/news/mark-read`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const markAdminNewsViewed = async () => {
  const response = await axios.post(`${API_URL}/admin/news/mark-viewed`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getNewsArticle = async (slug) => {
  const response = await axios.get(`${API_URL}/news/${slug}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Lymphocyte Trend API
export const getLymphocyteTrend = async (months = 24) => {
  const response = await axios.get(`${API_URL}/blood-results/lymphocyte-trend`, {
    params: { months },
    headers: getAuthHeader()
  });
  return response.data;
};

// Pathology Report Scan API
export const scanPathologyReport = async (imageBase64, mimeType = 'image/jpeg') => {
  const response = await axios.post(`${API_URL}/blood-results/scan-pathology`, {
    image_base64: imageBase64,
    mime_type: mimeType
  }, {
    headers: getAuthHeader(),
    timeout: 60000 // 60 second timeout for AI processing
  });
  return response.data;
};

// Create axios instance for default export
const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth interceptor for requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle expired tokens
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // If we get a 401 Unauthorized, the token is invalid/expired
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      // Don't redirect if already on login/register/public pages
      const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/', '/blog', '/preview'];
      const isPublicPath = publicPaths.some(path => currentPath === path || currentPath.startsWith('/blog/') || currentPath.startsWith('/preview/'));
      
      if (!isPublicPath) {
        // Clear stale auth data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Redirect to login with message
        window.location.href = '/login?session=expired';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
